<template>
  <div class="home">
    <AppNav />

    <header class="hero">
      <h1 class="hero-title">让工具更简单</h1>
      <p class="hero-sub">
        ColorThinking 出品的效率工具集——便签记录、项目管理、PDF 标注与远程终端，
        让信息井然有序，让工作随时随地继续。
      </p>
    </header>

    <section class="apps">
      <h2 class="section-title">我们的产品</h2>
      <div class="apps-grid">
        <div v-for="p in products" :key="p.id" class="app-card" :class="{ clickable: p.page }" @click="goPage(p)">
          <div class="app-info">
            <div class="app-logo">
              <img :src="p.logo" :alt="p.name">
            </div>
            <div class="app-content">
              <h3 class="app-name">{{ p.name }}</h3>
              <p class="app-mark">{{ p.mark }}</p>
              <div class="feature-list">
                <span v-for="feature in p.features" :key="feature" class="feature-chip">{{ feature }}</span>
              </div>
            </div>
          </div>
          <div v-if="p.buttons" class="platform-links" @click.stop>
                <template v-for="b in p.buttons" :key="b">
                  <v-btn v-if="b === 'ios' && p.iosDisabled" size="large" rounded="xl" variant="tonal" color="purple" class="platform-btn" disabled>
                    <div class="btn-content">
                      <img :src="appleIcon" alt="apple" class="btn-icon">
                      <span>苹果客户端</span>
                    </div>
                  </v-btn>
                  <v-btn v-else-if="b === 'ios'" size="large" rounded="xl" variant="tonal" color="purple" class="platform-btn" @click="goWeb(p.links.ios)">
                    <div class="btn-content">
                      <img :src="appleIcon" alt="apple" class="btn-icon">
                      <span>苹果客户端</span>
                    </div>
                  </v-btn>
                  <v-btn v-else-if="b === 'android' && p.androidDisabled" size="large" rounded="xl" variant="tonal" color="green" class="platform-btn" disabled>
                    <div class="btn-content">
                      <img :src="androidIcon" alt="android" class="btn-icon">
                      <span>安卓客户端</span>
                    </div>
                  </v-btn>
                  <v-btn v-else-if="b === 'android'" size="large" rounded="xl" variant="tonal" color="green" class="platform-btn" @click="downloadApk(p)">
                    <div class="btn-content">
                      <img :src="androidIcon" alt="android" class="btn-icon">
                      <span>安卓客户端</span>
                    </div>
                  </v-btn>
                  <v-btn v-else-if="b === 'web' && p.webDisabled" size="large" rounded="xl" variant="tonal" color="#03a9f4" class="platform-btn" disabled>
                    <div class="btn-content">
                      <img :src="webIcon" alt="web" class="btn-icon">
                      <span>网页端即将上线</span>
                    </div>
                  </v-btn>
                  <v-btn v-else-if="b === 'web'" size="large" rounded="xl" variant="tonal" color="#03a9f4" class="platform-btn" @click="goWeb(p.links.web)">
                    <div class="btn-content">
                      <img :src="webIcon" alt="web" class="btn-icon">
                      <span>网页端</span>
                    </div>
                  </v-btn>
                  <DesktopDownloadButton v-else-if="b === 'desktop'" :downloads="p.desktopDownloads" fill-grid />
                </template>
                <v-btn size="large" rounded="xl" variant="tonal" color="blue-grey" class="platform-btn" @click="goUpdates(p)">
                  <div class="btn-content">
                    <v-icon icon="mdi-history" size="20" />
                    <span>更新记录</span>
                  </div>
                </v-btn>
          </div>
        </div>
      </div>
    </section>

    <section class="company">
      <h2 class="section-title">关于 ColorThinking</h2>
      <p class="company-desc">
        我们致力于为用户提供简单、直观的辅助工具，帮助个人和团队更高效地完成工作，
        通过持续创新和优质服务，让每个人都能享受到科技带来的便利和效率提升。
      </p>
      <router-link to="/about" class="company-link">
        了解我们的团队
        <svg class="more-icon" viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <path d="M6 3.5 10.5 8 6 12.5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </router-link>
    </section>

    <AppFooter />
  </div>
</template>

<script setup>
import AppNav from '@/components/AppNav.vue'
import AppFooter from '@/components/AppFooter.vue'
import DesktopDownloadButton from '@/components/DesktopDownloadButton.vue'
import { products } from '@/data/products'
import { downloadApk } from '@/utils/downloadApk'
import { useRouter } from 'vue-router'
import appleIcon from '../assets/294700_apple_icon.png'
import androidIcon from '../assets/4879894_android_app_device_mobile_screen_icon (1).png'
import webIcon from '../assets/326663_language_web_icon (2).png'

const router = useRouter()

const goWeb = (link) => {
  window.location.href = link.trim()
}

const goPage = (p) => {
  if (p.page) router.push(`/home/${p.id}`)
}

const goUpdates = (p) => {
  router.push(`/updates/${p.id}`)
}

</script>

<style scoped>
.home {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: #fff;
  color: #1c2430;
}

/* Hero */
.hero {
  padding: 150px 24px 56px;
  text-align: center;
  animation: rise 0.7s ease both;
}

.hero-title {
  margin: 0 0 16px;
  font-size: clamp(2.1rem, 4.6vw, 3.3rem);
  font-weight: 800;
  letter-spacing: -0.025em;
  color: #171a21;
}

.hero-sub {
  max-width: 620px;
  margin: 0 auto;
  font-size: clamp(1rem, 1.6vw, 1.16rem);
  line-height: 1.75;
  color: #5c6470;
}

/* 产品区 */
.apps {
  padding: 20px 24px 70px;
}

.section-title {
  text-align: center;
  margin: 0 0 40px;
  font-size: clamp(1.5rem, 2.8vw, 2rem);
  font-weight: 800;
  letter-spacing: -0.02em;
  color: #1c2430;
}

.apps-grid {
  width: min(1180px, 100%);
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1.4rem;
}

.app-card {
  background: #fff;
  border: 1px solid #e7ebf0;
  border-radius: 24px;
  padding: 26px;
  box-shadow: 0 2px 10px rgba(20, 35, 65, 0.04);
  transition: box-shadow 0.25s ease, transform 0.25s ease;
}

.app-card.clickable {
  cursor: pointer;
}

.app-card.clickable:hover {
  box-shadow: 0 12px 32px rgba(20, 35, 65, 0.1);
  transform: translateY(-2px);
}

.app-info {
  display: flex;
  align-items: flex-start;
  gap: 1.25rem;
  margin-bottom: 0.8rem;
}

.app-logo {
  width: 92px;
  height: 92px;
  min-width: 92px;
  border-radius: 22px;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.1);
}

.app-logo img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 22px;
}

.app-content {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  gap: 0.7rem;
}

.app-name {
  margin: 0;
  font-size: 1.45rem;
  font-weight: 750;
  color: #1c2430;
  line-height: 1.25;
}

.app-mark {
  margin: 0;
  font-size: 0.92rem;
  line-height: 1.55;
  color: #5c6470;
  word-break: break-word;
  /* 简介最多两行，超出省略号截断，保证各卡片头部高度一致 */
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.feature-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.feature-chip {
  padding: 5px 10px;
  color: #2d5f95;
  font-size: 0.82rem;
  line-height: 1.2;
  border-radius: 7px;
  background: #eef6ff;
}

.more-icon {
  width: 12px;
  height: 12px;
  flex-shrink: 0;
  transition: transform 0.25s ease;
}

.company-link:hover .more-icon {
  transform: translateX(2px);
}

.platform-links {
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
  margin-top: 0.2rem;
}

/* 桌面按钮融入同一 flex 流，与平台按钮统一间距与胶囊风格 */
:deep(.desktop-download) {
  display: contents;
}

.btn-content {
  display: flex;
  align-items: center;
  gap: 8px;
}

.btn-icon {
  width: 20px;
  height: 20px;
  object-fit: contain;
}

/* 公司简介 */
.company {
  background: #f7f9fb;
  padding: 70px 24px;
  text-align: center;
}

.company .section-title {
  margin-bottom: 20px;
}

.company-desc {
  max-width: 640px;
  margin: 0 auto 26px;
  font-size: 1.02rem;
  line-height: 1.8;
  color: #5c6470;
}

.company-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #2d85f0;
  text-decoration: none;
  font-size: 0.98rem;
  font-weight: 600;
  transition: opacity 0.25s ease;
}

.company-link:hover {
  opacity: 0.75;
}

@keyframes rise {
  from {
    opacity: 0;
    transform: translateY(18px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (max-width: 1100px) {
  .apps-grid {
    grid-template-columns: 1fr;
    width: min(760px, 100%);
  }
}

@media (max-width: 768px) {
  .hero {
    padding: 118px 20px 44px;
  }

  .apps {
    padding: 10px 0.75rem 56px;
  }

  .app-card {
    padding: 16px;
    border-radius: 20px;
  }

  .app-info {
    gap: 1rem;
  }

  .app-logo {
    width: 108px;
    height: 108px;
    min-width: 108px;
    border-radius: 26px;
  }

  .app-logo img {
    border-radius: 26px;
  }

  .app-name {
    font-size: 1.25rem;
  }

  .app-mark {
    font-size: 0.86rem;
    text-align: left;
  }

  /* 标签单行显示，等分宽度，极端窄屏下省略号兜底 */
  .feature-list {
    flex-wrap: nowrap;
    gap: 4px;
  }

  .feature-chip {
    flex: 1 1 0;
    min-width: 0;
    display: flex;
    justify-content: center;
    overflow: hidden;
    white-space: nowrap;
    padding: 3px 4px;
    font-size: 0.68rem;
  }

  .more-icon {
    width: 11px;
    height: 11px;
  }

  /* 按钮区占满卡片整行宽度（已移出 app-content） */
  .platform-links {
    width: 100%;
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.5rem;
  }

  .platform-btn {
    min-width: 0;
    width: 100%;
    height: 40px;
    font-size: 0.82rem;
    padding: 0 10px;
  }

  :deep(.desktop-download .desktop-btn) {
    padding: 0 10px;
  }

  .btn-content {
    gap: 6px;
  }

  .btn-icon {
    width: 18px;
    height: 18px;
  }

  .company {
    padding: 56px 20px;
  }
}
</style>
